import { Alert } from "components/Alert/Alert"
import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { useRightSidebar } from "hooks/useRightSidebar"
import {
	ChannelPreference,
	UpdateNotificationPreferencesPayload,
} from "interface/notification"
import { useCallback, useMemo } from "react"
import {
	useNotificationPreferences,
	useUpdateNotificationPreferences,
} from "services/notifications"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"

export const NotificationSettings = () => {
	const { notify } = useNotification()
	const { data: preferences, isLoading } = useNotificationPreferences()
	const { mutate: updatePreferences, isPending } =
		useUpdateNotificationPreferences()
	const { setSidebarState } = useRightSidebar()
	const hasSmsPhone = preferences?.isSmsCapablePhoneOnFile ?? false
	const channels = useMemo<[string, Channel, ChannelPreference][]>(() => {
		if (!preferences) {
			return []
		}
		return [
			["In-app", "inApp", preferences.inApp],
			["Email", "email", preferences.email],
			["SMS", "sms", preferences.sms],
		]
	}, [preferences])
	const handleChange = useCallback(
		(channel: Channel, type: PreferenceType, checked: boolean) => {
			if (!preferences || isPending) {
				return
			}
			updatePreferences(
				{
					inApp: preferences.inApp,
					email: preferences.email,
					sms: preferences.sms,
					[channel]: {
						...preferences[channel],
						[type]: checked,
					},
				},
				{
					onError(error) {
						notify.error({
							title: error.message ?? GENERIC_ERROR_MESSAGE,
						})
					},
				},
			)
		},
		[isPending, notify, preferences, updatePreferences],
	)

	return (
		<Spinner spinning={isLoading}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col gap-6">
				<div className="flex flex-col gap-1">
					<h1
						id="notification-settings"
						className="font-headings text-3xl font-bold"
					>
						Notification settings
					</h1>
					<p>Update your global notification settings</p>
				</div>

				<div
					role="table"
					aria-labelledby="notification-settings"
					className="flex w-fit flex-col gap-2"
				>
					<div role="row" className="flex justify-between gap-1 py-4">
						<div role="columnheader" className="mr-4 min-w-3xs @3xl:min-w-md">
							Notification type
						</div>

						<div className="flex justify-evenly gap-4 px-4">
							{channels.map(([label]) => (
								<div
									key={label}
									role="columnheader"
									className="min-w-14 text-center"
								>
									{label}
								</div>
							))}
						</div>
					</div>

					<div
						role="rowgroup"
						className="divide-y divide-grey-10 rounded-2xl border border-grey-10"
					>
						{ROWS.map(([title, description, type, disabledChannels]) => (
							<div
								key={title}
								role="row"
								className="flex items-center justify-between gap-1 p-4"
							>
								<div role="rowheader" className="min-w-3xs @3xl:min-w-md">
									<div className="font-bold">{title}</div>
									<p>{description}</p>
								</div>

								<div className="flex justify-evenly gap-4">
									{channels.map(([channelName, channel, preference]) => (
										<Wrap
											key={`${channel}-${type}`}
											condition={channel === "sms" && !hasSmsPhone}
											with={(children) => (
												<Tooltip title="No SMS-capable phone number on file">
													{children}
												</Tooltip>
											)}
										>
											<div role="cell" className="min-w-14 text-center">
												<Checkbox
													disabled={
														isPending ||
														(channel === "sms" && !hasSmsPhone) ||
														disabledChannels.includes(channel)
													}
													checked={preference[type] ?? false}
													aria-label={`${channelName} notification for ${title}`}
													onChange={(e) =>
														handleChange(channel, type, e.target.checked)
													}
												/>
											</div>
										</Wrap>
									))}
								</div>
							</div>
						))}
					</div>
				</div>

				<Alert
					showIcon
					type="info"
					className="w-fit"
					title={
						<>
							Need to update your phone number? For now, that goes through a{" "}
							<Button
								type="link"
								className="h-auto p-0 font-medium text-navy-100 underline"
								onClick={() =>
									setSidebarState({
										key: "support",
										onBehalfOf: false,
										initializeCreate: true,
									})
								}
							>
								support ticket
							</Button>{" "}
							– we’re working on making this self-service soon!
						</>
					}
				/>
			</div>
		</Spinner>
	)
}

type Channel = keyof UpdateNotificationPreferencesPayload
type PreferenceType = keyof ChannelPreference

const ROWS: [string, string, PreferenceType, Channel[]][] = [
	[
		"Escalation assignment",
		"Updates and reminders when assigned an escalation",
		"isAssignmentEnabled",
		["inApp", "email"],
	],
	[
		"Escalation subscription",
		"New activity on escalations you subscribed to",
		"isSubscriptionEnabled",
		[],
	],
]
